import React, {useEffect, useRef} from 'react';
import FormRequest from './FormRequest';
import {useParams, useSearchParams} from 'react-router-dom';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
    useSubmitPublicFormMutation,
    useLazyCheckContactEmailForStudentPublicLinkedRequestQuery
} from '../../../../api/services/formRequestsService';
import {useGetPublicFormQuery} from '../../../../api/services/formsService';
import {showSnackbar} from '../../../../AppLayout/ApplicationSlice';
import {useDispatch} from 'react-redux';
import PropTypes from 'prop-types';
import {FORM_ACCESS_LEVELS} from '../../../../constants/formAccessLevels';
import objectToFormData from '../../../../utility/objectToFormData';
import {generateUUID} from '../../../../utility/uuidUtil';

const parseData = (encodedData, searchParams) => {
    try {
        if (encodedData) {
            const padding = encodedData.length % 4;
            const base64 = encodedData.replace(/-/g, '+')
                .replace(/_/g, '/')
                .padEnd(encodedData.length + (padding === 0 ? 0 : 4 - padding), '=');

            return JSON.parse(atob(base64));
        } else {
            return {
                t: searchParams.get('t'),
                parent_id: searchParams.get('parent_id')
            };
        }
    } catch (error) {
        return {
            t: '',
            parent_id: ''
        };
    }
};

const SubmitPublicForm = ({navigateBackRoute, type}) => {
    const [searchParams] = useSearchParams();
    const {id, encodedData} = useParams();
    const dispatch = useDispatch();
    const contentRef = useRef(null);
    const metadata = parseData(encodedData, searchParams);

    const {
        data: form,
        isLoading,
        isFetching,
        error
    } = useGetPublicFormQuery({
        id,
        type,
        queryParams: {
            t: metadata.t,
            parent_id: metadata.parent_id
        }
    }, {
        refetchOnMountOrArgChange: true
    });

    const [submitPublicForm, {
        isSuccess: isFormSubmitSuccess,
        isError: isFormSubmitError,
        error: submitError,
        isLoading: isSubmitting
    }] = useSubmitPublicFormMutation();

    const [checkContactEmailPublicLinked] = useLazyCheckContactEmailForStudentPublicLinkedRequestQuery();
    const onEmailCheck = async (email) => {
        const trimmed = (email ?? '').trim();
        if (!trimmed) return null;

        if (type !== FORM_ACCESS_LEVELS.publicWithStudentLink) return null;

        try {
            return await checkContactEmailPublicLinked({
                requestId: Number(id),
                email: trimmed,
                tag: metadata.t
            }).unwrap();
        } catch {
            return null;
        }
    };

    useEffect(() => {
        if (isFormSubmitError) {
            dispatch(showSnackbar({
                message: submitError?.message ?? submitError ?? 'Failed to submit form.',
                severity: 'error'
            }));
        }
    }, [dispatch, isFormSubmitError, submitError]);

    const sendHeight = () => {
        const height = Math.max(
            document.documentElement.scrollHeight,
            document.querySelector('.modal')?.scrollHeight ?? 0
        );

        window.parent.postMessage({
            type: 'iframeResizer',
            iframeHeight: height
        }, '*');
    };

    useEffect(() => {
        const resizeObserver = new ResizeObserver(sendHeight);
        if (contentRef.current) {
            resizeObserver.observe(contentRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    const FormSubmittedSuccessfullyView = () =>
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            width="100%"
        >
            <TaskAltRoundedIcon color="success" sx={{fontSize: 64}}/>
            <Typography variant="h5" gutterBottom>Success</Typography>
            <Typography>Form successfully submitted.</Typography>
        </Box>;

    const handleSubmit = async (data) => {
        const responses = {};
        const attachments = [];
        const attachmentKeys = [];

        for (const key in data) {
            if (data[key] instanceof Blob) {
                const file = data[key];
                const fileKey = generateUUID();

                attachments.push(file);
                attachmentKeys.push(fileKey);
                responses[key] = {name: file.name, fileKey};
            } else {
                responses[key] = data[key];
            }
        }

        const payload = type === FORM_ACCESS_LEVELS.publicWithStudentLink
            ? {
                formRequestId: id,
                tag: metadata.t,
                placeholders: JSON.stringify(form.placeholderValues),
                responses: JSON.stringify(responses),
                attachments,
                attachmentKeys,
            }
            : {
                formId: id,
                isCreateApplicantForm: form.schema?.isCreateApplicantForm,
                responses: JSON.stringify(responses),
                attachments,
                attachmentKeys,
            };

        await submitPublicForm(objectToFormData(payload));
    };

    return <div ref={contentRef}>
        {
            isFormSubmitSuccess
                ? <FormSubmittedSuccessfullyView sx={{color: 'theme.palette.success.main'}}/>
                : <FormRequest
                    publicForm
                    formRequest={form}
                    // Only the student-link route carries a form request in the URL; on the plain
                    // public route `id` is the form itself and no request exists yet. A Stripe
                    // Payment question needs the request to raise the payment against, so it stays
                    // disabled there rather than pretending it can charge.
                    formRequestId={type === FORM_ACCESS_LEVELS.publicWithStudentLink ? id : undefined}
                    isLoading={isLoading || isFetching}
                    isSubmitting={isSubmitting}
                    error={error}
                    onSubmit={handleSubmit}
                    navigateBackRoute={navigateBackRoute}
                    onEmailCheck={onEmailCheck}
                />
        }
    </div>;
};

SubmitPublicForm.propTypes = {
    navigateBackRoute: PropTypes.string.isRequired,
    type: PropTypes.number.isRequired,
};

export default SubmitPublicForm;
